<template>
  <div class="klarna-return">
    <div class="klarna-return-content">
      <!-- Loading -->
      <div v-if="loading" class="klarna-loading">
        <svg xmlns="http://www.w3.org/2000/svg" width="50" height="50" viewBox="0 0 100 100">
          <circle cx="50" cy="50" fill="none" stroke="#2e9bf0" stroke-width="10" r="35" stroke-dasharray="165 57">
            <animateTransform attributeName="transform" type="rotate" repeatCount="indefinite" dur="0.5s" values="0 50 50;360 50 50" keyTimes="0;1" />
          </circle>
        </svg>
        <p class="klarna-status-text">Confirming your payment...</p>
      </div>

      <!-- Error -->
      <div v-if="error" class="klarna-error">
        <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#ef4444" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="12" y1="8" x2="12" y2="12"></line>
          <line x1="12" y1="16" x2="12.01" y2="16"></line>
        </svg>
        <h2>Payment Not Completed</h2>
        <p>{{ errorMessage }}</p>
        <button class="klarna-retry-btn" @click="goBackToCheckout">
          Return to Checkout <span>-&gt;</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { loadStripe } from "@stripe/stripe-js";

export default {
  name: 'KlarnaReturn',
  data() {
    return {
      loading: true,
      error: false,
      errorMessage: '',
      baseUrl: process.env.VUE_APP_API_ENDPOINT,
    };
  },
  async mounted() {
    await this.confirmPayment();
  },
  methods: {
    async confirmPayment() {
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const clientSecret = urlParams.get('payment_intent_client_secret');
        const paymentIntentId = urlParams.get('payment_intent');

        if (!clientSecret || !paymentIntentId) {
          this.error = true;
          this.errorMessage = 'Missing payment information. Please try again.';
          this.loading = false;
          return;
        }

        // Check PaymentIntent status via Stripe.js
        const stripe = await loadStripe(process.env.VUE_APP_STRIPE_KEY);
        const { paymentIntent } = await stripe.retrievePaymentIntent(clientSecret);

        if (paymentIntent.status === 'succeeded') {
          // Confirm on backend and fulfill order
          const response = await this.$axios.post(`${this.baseUrl}/api/confirm-klarna-payment`, {
            paymentIntentId: paymentIntentId,
          });

          if (response.data.status === 'success') {
            await this.$store.dispatch('setPrescriptionPreauths', response.data.prescriptionPreauths);

            const amountCharged = (paymentIntent.amount / 100) - (response.data.discount || 0);
            const transactionIdentifier = response.data.transaction_id ||
              (response.data.prescriptionPreauths?.[0]?.transactionId) ||
              `${response.data.customerId}_${Date.now()}`;

            this.$router.push({
              name: 'prescriptionThanks',
              query: {
                terms: String(response.data.payment_terms || 1),
                amount: amountCharged,
                trans_id: transactionIdentifier,
              },
            });
          } else {
            this.error = true;
            this.errorMessage = response.data.message || 'Order fulfillment failed. Please contact support.';
          }
        } else if (paymentIntent.status === 'requires_payment_method') {
          this.error = true;
          this.errorMessage = 'Payment was not approved. Please try again with a different payment method.';
        } else {
          this.error = true;
          this.errorMessage = `Payment status: ${paymentIntent.status}. Please contact support if you were charged.`;
        }
      } catch (error) {
        console.error('Klarna confirmation error:', error);
        this.error = true;
        this.errorMessage = error.response?.data?.message || 'Failed to confirm payment. Please contact support.';
      } finally {
        this.loading = false;
      }
    },
    goBackToCheckout() {
      const cartId = this.$route.params.cart_id;
      this.$router.push({ name: 'checkoutWeightLoss', params: { cart_id: cartId } });
    },
  },
};
</script>

<style scoped>
.klarna-return {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8f9fa;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.klarna-return-content {
  text-align: center;
  padding: 40px;
  max-width: 480px;
}

.klarna-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
}

.klarna-status-text {
  font-size: 18px;
  color: #374151;
  font-weight: 500;
}

.klarna-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.klarna-error h2 {
  font-size: 22px;
  color: #1f2937;
  margin: 0;
}

.klarna-error p {
  font-size: 16px;
  color: #6b7280;
  margin: 0;
}

.klarna-retry-btn {
  margin-top: 8px;
  padding: 14px 32px;
  background: #00A36C;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
}

.klarna-retry-btn:hover {
  opacity: 0.9;
}
</style>
